import { UNITS } from "./formula-units";
import { getMetricValuesFromOldBagData } from "./formula-helpers";
import { referenceMetricValueOrCompute, sumRowUnitsValues } from "../sibling-value-getter/getter";
import { MetricsKind } from "../types";
import { ColumnTypes } from "components/product-collection/product-collection-views/product-collection-grid-view/product-collection-grid-view-helper";
import { ValueGetterParams } from "ag-grid-community";
import { safeParseFloat } from "lib/number";

/**
 * Formulas for Metrics
 *
 * @param factoryOptions
 *  - provided to `metricsValueGetterCreator`
 *  - untyped, easily allow for custom inputs needed for calculation
 * @param getterParams
 *  - default getterParams from AgGrid
 *  - has context and data etc to facilitate calculation
 * @returns
 *  - a numerical caculated metrics value
 */
export const UNIT_DOOR_PERCENTAGE_PLAN = ({ factoryOptions, getterParams }) => {
    const { node, column } = getterParams as ValueGetterParams;
    const { useSize } = factoryOptions;

    const units = referenceMetricValueOrCompute(
        () => safeParseFloat(UNITS({ factoryOptions, getterParams }), 0),
        getterParams,
        MetricsKind.UNITS
    );

    // For grand total, this will always be 100
    if (units > 0 && column.getColDef().type === ColumnTypes.AllDimensionTotal) {
        return 100;
    }

    const grandTotalUnits = referenceMetricValueOrCompute(
        () => {
            if (getterParams.context.allocationBagRef?.allocationBag) {
                return sumRowUnitsValues(factoryOptions, getterParams);
            } else {
                let total = 0;
                const { data } = getterParams;
                const { deliveryId, filteredDeliveriesForAssortmentSet, filteredDoorsForAssortmentSet } =
                    factoryOptions;

                /**
                 * Old allocation bag
                 */
                let cols: string[][] = [];
                const size: string = data.size;
                if (data.allocations && data.allocations.current) {
                    cols = data.allocations.current.r.map((r) => r.c);
                } else if (data.bulks && data.bulks.r) {
                    cols = data.bulks.r.map((r) => r.c);
                } else if (data.bulks.current && data.bulks.current.r) {
                    cols = data.bulks.current.r.map((r) => r.c);
                }

                cols.forEach((col) => {
                    const { doorIdValue, currentDeliveryIdValue, bulkCount, currentSize } =
                        getMetricValuesFromOldBagData(deliveryId, filteredDoorsForAssortmentSet, col, useSize, size);

                    if (useSize && size !== currentSize) {
                        return;
                    }

                    // If there is a filter check to make sure this delivery or door is in that list
                    if (
                        filteredDeliveriesForAssortmentSet &&
                        !filteredDeliveriesForAssortmentSet.has(currentDeliveryIdValue)
                    ) {
                        return;
                    }

                    if (filteredDoorsForAssortmentSet && !filteredDoorsForAssortmentSet.has(doorIdValue)) {
                        return;
                    }

                    total += bulkCount;
                });

                return total;
            }
        },
        getterParams,
        MetricsKind.UNITS,
        node.id,
        ColumnTypes.AllDimensionTotal
    );

    if (grandTotalUnits === 0) {
        return 0;
    }

    if (!units) {
        return 0;
    }

    return (units / grandTotalUnits) * 100;
};
